import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import * as transactionService from "../services/transactionService";
import * as paymentService from "../services/paymentService";
import type {
  Transaction,
  Payment,
  PaymentMethod,
  CreatePaymentPayload,
} from "../types/api";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { Copy } from "lucide-react";
import { TransactionsSparkline } from "../components/charts/TransactionsSparkline";
import { Skeleton } from "../components/ui/skeleton";
import { cn } from "../lib/utils";
import { useTranslation } from "react-i18next";
import DepositFormModal from "../components/DepositFormModal";
import type { PaymentFormData } from "../components/PaymentFormModal";
import StripePaymentModal from "../components/StripePaymentModal";
import { Pagination } from "../components/table/DataTable";

const TransactionHistoryPage = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [currentPayment, setCurrentPayment] = useState<Payment | null>(null);
  const [stripePayment, setStripePayment] = useState<Payment | null>(null);
  const [confirmationValue, setConfirmationValue] = useState("");
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const { t } = useTranslation();
  const getPaymentErrorMessage = (error: unknown) => {
    if (typeof error === "string") {
      const lowered = error.toLowerCase();
      if (lowered.includes("wallet crypto") || lowered.includes("adresse de réception")) {
        return t(
          "payment.form.cryptoWalletMissing",
          "L'adresse du réseau choisi n'est pas configurée."
        );
      }
      return error;
    }
    return t("transactions.paymentInitErrorDescription");
  };
  const resolveCryptoNetworkLabel = (payment?: Payment | null) => {
    if (!payment?.metadata) return null;
    try {
      const parsed = JSON.parse(payment.metadata);
      const network =
        typeof parsed?.cryptoNetwork === "string"
          ? parsed.cryptoNetwork.toUpperCase()
          : null;
      const asset =
        typeof parsed?.cryptoAsset === "string"
          ? parsed.cryptoAsset.toUpperCase()
          : null;
      if (asset === "ETH" || network === "ERC20") {
        return t("payment.form.cryptoNetworkEthLabel", "ERC20 (ETH)");
      }
      if (asset === "BNB" || network === "BSC") {
        return t("payment.form.cryptoNetworkBnbLabel", "BSC (BEP20)");
      }
      if (asset === "BTC" || network === "BTC") {
        return t("payment.form.cryptoNetworkBtcLabel", "BTC (mainnet)");
      }
      return network;
    } catch {
      return null;
    }
  };
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const transactionsPerPage = 8;
  const paymentsPerPage = 6;
  const totalTransactionsPages = Math.max(
    1,
    Math.ceil(transactions.length / transactionsPerPage)
  );
  const totalPaymentsPages = Math.max(
    1,
    Math.ceil(payments.length / paymentsPerPage)
  );
  const paginatedTransactions = useMemo(() => {
    if (transactions.length === 0) return [];
    const start = (transactionsPage - 1) * transactionsPerPage;
    return transactions.slice(start, start + transactionsPerPage);
  }, [transactions, transactionsPage, transactionsPerPage]);
  const paginatedPayments = useMemo(() => {
    if (payments.length === 0) return [];
    const start = (paymentsPage - 1) * paymentsPerPage;
    return payments.slice(start, start + paymentsPerPage);
  }, [payments, paymentsPage, paymentsPerPage]);

  const transactionStatusLabel = (status: Transaction["status"]) => {
    switch (status) {
      case "completed":
        return t("transactions.statusCompleted");
      case "failed":
        return t("transactions.statusFailed");
      default:
        return t("transactions.statusPending");
    }
  };

  const transactionStatusPillClass = (status: Transaction["status"]) =>
    status === "completed"
      ? "bg-green-500/20 text-green-800 dark:text-green-300"
      : status === "failed"
      ? "bg-red-500/20 text-red-800 dark:text-red-300"
      : "bg-yellow-500/20 text-yellow-900 dark:text-yellow-200";

  const fetchTransactions = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const data = await transactionService.listTransactions(user.id);
      setTransactions(data);
    } catch (err) {
      toast({
        title: t("transactions.errorTitle", "Erreur"),
        description:
          (err as string) ??
          t(
            "transactions.loadTransactionsError",
            "Impossible de charger les transactions"
          ),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, t, user]);

  const fetchPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      const data = await paymentService.listPayments();
      setPayments(data);
    } catch (err) {
      toast({
        title: t("transactions.errorTitle", "Erreur"),
        description:
          (err as string) ??
          t(
            "transactions.loadPaymentsError",
            "Impossible de charger vos paiements"
          ),
        variant: "destructive",
      });
    } finally {
      setLoadingPayments(false);
    }
  }, [toast, t]);

  useEffect(() => {
    void fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    if (transactionsPage > totalTransactionsPages) {
      setTransactionsPage(totalTransactionsPages);
    }
  }, [transactionsPage, totalTransactionsPages]);

  useEffect(() => {
    if (paymentsPage > totalPaymentsPages) {
      setPaymentsPage(totalPaymentsPages);
    }
  }, [paymentsPage, totalPaymentsPages]);

  useEffect(() => {
    void fetchPayments();
  }, [fetchPayments]);

  const handlePaymentInit = async (
    data: PaymentFormData & { amount: number; currency: string }
  ) => {
    setIsSubmitting(true);
    try {
      const payment = await paymentService.createPayment({
        amount: data.amount,
        currency: data.currency.toUpperCase(),
        method: data.method,
        metadata: JSON.stringify({
          cardholderName: data.cardholderName || null,
          billingCountry: data.country || null,
          cryptoAsset: data.method === "crypto" ? data.cryptoAsset : null,
          cryptoNetwork: data.method === "crypto" ? data.cryptoNetwork : null,
        }),
      });
      setConfirmationValue("");
      setDepositModalOpen(false);

      if (payment.method === "card" && payment.clientSecret) {
        setStripePayment(payment);
      } else {
        setCurrentPayment(payment);
      }
      toast({
        title: t("transactions.paymentInitTitle"),
        description:
          payment.method === "card"
            ? t("transactions.paymentInitCardDescription")
            : t("transactions.paymentInitCryptoDescription"),
        variant: "success",
      });
      await fetchPayments();
      await fetchTransactions();
    } catch (err) {
      toast({
        title: t("transactions.errorTitle", "Erreur"),
        description: getPaymentErrorMessage(err),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStripeSuccess = async (paymentIntentId: string) => {
    if (!stripePayment) return;
    try {
      const payment = await paymentService.confirmPayment(stripePayment.id, {
        confirmationValue: paymentIntentId,
      });
      setStripePayment(null);
      if (payment.status === "confirmed") {
        toast({
          title: t("transactions.paymentConfirmTitle"),
          description: t("transactions.paymentConfirmDescription"),
          variant: "success",
        });
        await fetchPayments();
        await fetchTransactions();
      }
    } catch (error) {
      toast({
        title: t("transactions.errorTitle", "Erreur"),
        description:
          (error as string) ??
          t("transactions.paymentConfirmErrorDescription"),
        variant: "destructive",
      });
    }
  };

  const handleConfirmPayment = async () => {
    if (!currentPayment) {
      toast({
        title: t("transactions.noPaymentToConfirmTitle"),
        variant: "destructive",
      });
      return;
    }

    // Vérifier si le paiement nécessite l'approbation du manager
    // Pour les paiements en "requires_action", seul le manager peut confirmer
    if (currentPayment.status === "requires_action") {
      const isManager =
        user?.role === "manager_contact" || user?.role === "manager_trader";
      if (!isManager) {
        toast({
          title: t(
            "transactions.managerApprovalRequiredTitle",
            "Approbation manager requise"
          ),
          description: t(
            "transactions.managerApprovalRequiredDescription",
            "Ce paiement nécessite l'approbation de votre manager. Vous ne pouvez pas le confirmer vous-même."
          ),
          variant: "destructive",
        });
        return;
      }
    }

    // Pour les paiements crypto, pas besoin de confirmationValue
    if (currentPayment.method === "card" && !confirmationValue.trim()) {
      toast({
        title: t("transactions.confirmationRequiredTitle"),
        description: t("transactions.confirmationRequiredCardDescription"),
        variant: "destructive",
      });
      return;
    }

    setIsConfirming(true);
    try {
      const payment = await paymentService.confirmPayment(currentPayment.id, {
        confirmationValue: currentPayment.method === "crypto" ? "" : confirmationValue,
      });
      setCurrentPayment(payment);
      setConfirmationValue("");
      if (payment.status === "confirmed") {
        toast({
          title: t("transactions.paymentConfirmedTitle"),
          description: t("transactions.paymentConfirmedDescription"),
          variant: "success",
        });
      } else if (
        payment.status === "requires_action" &&
        currentPayment.method === "crypto" &&
        !(user?.role === "manager_contact" || user?.role === "manager_trader")
      ) {
        toast({
          title: t(
            "transactions.cryptoPaymentSubmittedTitle",
            "Paiement en attente"
          ),
          description: t(
            "transactions.cryptoPaymentSubmittedDescription",
            "Votre paiement a été confirmé. Un code unique a été envoyé à votre manager pour validation. Votre compte sera crédité après approbation."
          ),
          variant: "info",
        });
      }
      await Promise.all([fetchTransactions(), fetchPayments()]);
    } catch (err) {
      toast({
        title: t("transactions.errorTitle", "Erreur"),
        description:
          (err as string) ?? t("transactions.confirmPaymentErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancelPayment = async () => {
    if (!currentPayment) {
      toast({
        title: t("transactions.noPaymentToCancelTitle", "Aucun paiement"),
        variant: "destructive",
      });
      return;
    }

    // Vérifier que le paiement peut être annulé
    if (currentPayment.status === "confirmed") {
      toast({
        title: t(
          "transactions.cannotCancelConfirmedTitle",
          "Impossible d'annuler"
        ),
        description: t(
          "transactions.cannotCancelConfirmedDescription",
          "Un paiement confirmé ne peut pas être annulé"
        ),
        variant: "destructive",
      });
      return;
    }

    if (currentPayment.status === "cancelled") {
      toast({
        title: t("transactions.alreadyCancelledTitle", "Déjà annulé"),
        description: t(
          "transactions.alreadyCancelledDescription",
          "Ce paiement a déjà été annulé"
        ),
        variant: "destructive",
      });
      return;
    }

    setIsCancelling(true);
    try {
      const payment = await paymentService.cancelPayment(currentPayment.id);
      // Réinitialiser le paiement sélectionné si annulé pour masquer le formulaire
      if (payment.status === "cancelled") {
        setCurrentPayment(null);
        setConfirmationValue("");
      } else {
        setCurrentPayment(payment);
      }
      // Rafraîchir la liste des paiements pour mettre à jour le statut
      await fetchPayments();
      toast({
        title: t("transactions.paymentCancelledTitle", "Paiement annulé"),
        description: t(
          "transactions.paymentCancelledDescription",
          "Le paiement a été annulé avec succès"
        ),
        variant: "success",
      });
      await Promise.all([fetchTransactions(), fetchPayments()]);
    } catch (err) {
      toast({
        title: t("transactions.errorTitle", "Erreur"),
        description:
          (err as string) ??
          t(
            "transactions.cancelPaymentErrorDescription",
            "Impossible d'annuler le paiement"
          ),
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const paymentInstructions = useMemo(() => {
    if (!currentPayment) {
      return (
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {t("transactions.instructionsEmpty")}
        </p>
      );
    }

    if (currentPayment.method === "card") {
      return (
        <>
          <p className="text-sm text-white/80">
            {t("transactions.instructionsCardText", {
              provider: "MockPay",
            })}{" "}
            <a
              className="text-cyan-300 underline"
              href={currentPayment.paymentUrl ?? "#"}
              target="_blank"
              rel="noreferrer">
              MockPay
            </a>
          </p>
          <ul className="mt-3 space-y-2 text-sm text-white/70">
            <li>
              {t("transactions.instructionsClientSecret")}{" "}
              <code className="rounded bg-black/40 px-2 py-1">
                {currentPayment.clientSecret}
              </code>
            </li>
            <li>
              {t("transactions.instructionsVerificationCode")}{" "}
              <span className="rounded-full bg-cyan-500/20 px-3 py-1 text-cyan-200">
                {currentPayment.verificationCode}
              </span>
            </li>
          </ul>
        </>
      );
    }

    const cryptoNetwork = resolveCryptoNetworkLabel(currentPayment);
    return (
      <>
        <p className="text-sm text-white/80">
          {t("transactions.instructionsCryptoText", {
            amount: currentPayment.amount,
            currency: currentPayment.currency,
          })}{" "}
          <span className="font-semibold">
            {currentPayment.amount} {currentPayment.currency}
          </span>{" "}
          vers l'adresse suivante puis indiquez le hash de transaction.
        </p>
        {cryptoNetwork && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground dark:text-white/60">
            <span>{t("payment.form.cryptoNetworkLabel", "Réseau")}:</span>
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] uppercase text-muted-foreground dark:border-white/10 dark:bg-white/10 dark:text-white/70">
              {cryptoNetwork}
            </span>
          </div>
        )}
        <p className="text-xs text-amber-800 dark:text-amber-300/90">
          {t(
            "payment.form.cryptoNetworkWarning",
            "⚠️ Sélectionnez exactement le même réseau que celui de votre wallet (ex: Binance), sinon les fonds peuvent être perdus."
          )}
        </p>
        <div className="mt-3 rounded-xl border border-border bg-muted/50 p-3 text-xs text-muted-foreground dark:border-white/10 dark:bg-black/30 dark:text-white/70">
          <p className="font-semibold uppercase text-muted-foreground dark:text-white/60">
            {t("transactions.instructionsAddress")}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 break-all text-foreground">{currentPayment.walletAddress}</code>
            <Button
              type="button"
              variant="ghost"
              aria-label={t("accounts.copyButton", "Copier")}
              className="h-8 w-8 shrink-0 rounded-md border border-border bg-muted/60 p-0 text-muted-foreground hover:bg-muted hover:text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(
                    currentPayment.walletAddress || ""
                  );
                  toast({
                    title: t("accounts.copySuccessTitle", "Copié"),
                    description: t(
                      "accounts.copySuccessDescription",
                      "Adresse copiée."
                    ),
                  });
                } catch {
                  toast({
                    title: t("accounts.copyErrorTitle", "Erreur"),
                    description: t(
                      "accounts.copyErrorDescription",
                      "Impossible de copier l'adresse."
                    ),
                    variant: "destructive",
                  });
                }
              }}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(
              currentPayment.walletAddress ?? ""
            )}`}
            alt={t("transactions.instructionsAddress")}
            className="mt-3 h-24 w-24 rounded-md bg-white p-1"
          />
        </div>
      </>
    );
  }, [currentPayment]);

  if (!user) {
    return <p>{t("transactions.loginRequired")}</p>;
  }

  return (
    <section className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[1fr,280px] lg:items-center">
        <div>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-semibold text-foreground">
                {t("transactions.title")}
              </h2>
              <p className="text-sm text-muted-foreground dark:text-white/70">
                {t("transactions.subtitle")}
              </p>
            </div>
            <span className="hidden rounded-full border border-border bg-muted/50 px-3 py-1 text-xs text-muted-foreground sm:inline dark:border-white/10 dark:bg-white/5 dark:text-white/60">
              {t("transactions.countLabel", { count: transactions.length })}
            </span>
          </div>
        </div>
        <div className="rounded-3xl border border-border bg-muted/50 p-4 dark:border-white/10 dark:bg-black/30">
          <p className="text-xs uppercase tracking-widest text-muted-foreground dark:text-white/60">
            {t("transactions.recentFlows")}
          </p>
          <TransactionsSparkline data={transactions} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="mx-auto flex w-full max-w-xl flex-col gap-6 rounded-3xl border border-border bg-card p-6 dark:border-white/10 dark:bg-white/5">
          <div className="text-center space-y-1">
            <h3 className="text-2xl font-semibold text-foreground dark:text-white">
              {t("transactions.depositTitle")}
            </h3>
            <p className="text-sm text-muted-foreground dark:text-white/70">
              {t("transactions.depositSubtitle")}
            </p>
          </div>

          <Button
            onClick={() => setDepositModalOpen(true)}
            className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white"
            disabled={isSubmitting}>
            {isSubmitting
              ? t("transactions.initPaymentProcessing")
              : t("transactions.initPayment")}
          </Button>
        </div>

        <div className="space-y-4 rounded-3xl border border-border bg-muted/50 p-6 dark:border-white/10 dark:bg-black/30">
          <div>
            <h3 className="text-2xl font-semibold text-foreground dark:text-white">
              {t("transactions.instructionsTitle")}
            </h3>
            <p className="text-sm text-muted-foreground dark:text-white/70">
              {t("transactions.instructionsSubtitle")}
            </p>
          </div>

          <div className="space-y-3 rounded-2xl border border-border bg-card p-4 text-foreground/90 dark:border-white/10 dark:bg-white/5 dark:text-white/80">
            {paymentInstructions}
          </div>

          {currentPayment &&
            currentPayment.status !== "confirmed" &&
            currentPayment.status !== "cancelled" && (
              <div className="space-y-3 rounded-2xl border border-border bg-card p-4 dark:border-white/10 dark:bg-white/5">
                {/* Afficher un message si le paiement nécessite l'approbation du manager */}
                {currentPayment.status === "requires_action" &&
                  user?.role !== "manager_contact" &&
                  user?.role !== "manager_trader" && (
                    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3">
                      <p className="text-sm text-yellow-900 dark:text-yellow-300">
                        {t(
                          "transactions.managerApprovalRequiredMessage",
                          "Ce paiement nécessite l'approbation de votre manager. Vous ne pouvez pas le confirmer vous-même."
                        )}
                      </p>
                    </div>
                  )}

                {/* Afficher les champs de confirmation seulement si l'utilisateur peut confirmer */}
                {(currentPayment.status !== "requires_action" ||
                  user?.role === "manager_contact" ||
                  user?.role === "manager_trader") && (
                  <>
                    {currentPayment.method === "card" && (
                      <>
                        <Label className="text-sm text-muted-foreground dark:text-white/70">
                          {t("transactions.verificationInputLabelCard")}
                        </Label>
                        <Input
                          value={confirmationValue}
                          onChange={(event) =>
                            setConfirmationValue(event.target.value)
                          }
                          placeholder={t("transactions.verificationInputPlaceholderCard")}
                        />
                      </>
                    )}
                    {currentPayment.method === "crypto" && (
                      <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-4">
                        <p className="text-sm text-cyan-800 dark:text-cyan-400">
                          {t(
                            "payment.confirm.cryptoInstructions",
                            "Confirmez votre paiement. Un code unique sera envoyé à votre manager pour validation."
                          )}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        className="flex-1"
                        onClick={handleConfirmPayment}
                        disabled={isConfirming || isCancelling || (currentPayment.method === "card" && !confirmationValue.trim())}>
                        {isConfirming
                          ? t("transactions.initPaymentProcessing")
                          : t("transactions.confirmPaymentButton")}
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        className="flex-1"
                        onClick={handleCancelPayment}
                        disabled={isConfirming || isCancelling}>
                        {isCancelling
                          ? t("transactions.cancellingPayment", "Annulation...")
                          : t("transactions.cancelPaymentButton", "Annuler")}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

          {currentPayment && currentPayment.status === "confirmed" && (
            <p className="text-sm text-green-700 dark:text-green-400">
              {t("transactions.paymentConfirmedBanner")}
            </p>
          )}
          {currentPayment && currentPayment.status === "cancelled" && (
            <p className="text-sm text-red-700 dark:text-red-400">
              {t(
                "transactions.paymentCancelledBanner",
                "Ce paiement a été annulé"
              )}
            </p>
          )}
        </div>
      </div>

      <div className="rounded-3xl border border-border bg-muted/50 p-6 dark:border-white/10 dark:bg-black/30">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-semibold text-foreground dark:text-white">
            {t("transactions.paymentsTitle")}
          </h3>
          <span className="text-xs text-muted-foreground dark:text-white/60">
            {t("transactions.paymentsCountLabel", {
              count: payments.length,
            })}
          </span>
        </div>
        {loadingPayments ? (
          <div className="mt-4 space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : payments.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground dark:text-white/60">
            {t("transactions.paymentsEmpty")}
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {paginatedPayments.map((payment) => {
              const isSelected = currentPayment?.id === payment.id;
              const isActionable =
                payment.status === "requires_action" ||
                payment.status === "pending" ||
                payment.status === "cancelled"; // Permettre de voir les paiements annulés
              const canCancel =
                payment.status === "requires_action" ||
                payment.status === "pending";
              let stripeFees:
                | {
                    fee: number;
                    net: number;
                    currency: string;
                    exchangeRate?: number | null;
                  }
                | null = null;
              if (payment.metadata) {
                try {
                  const parsed = JSON.parse(payment.metadata) as {
                    stripeFees?: {
                      fee: number;
                      net: number;
                      currency: string;
                      exchangeRate?: number | null;
                    };
                  };
                  if (parsed?.stripeFees) {
                    stripeFees = parsed.stripeFees;
                  }
                } catch {
                  stripeFees = null;
                }
              }

              return (
                <article
                  key={payment.id}
                  onClick={() => {
                    if (!isActionable) return;
                    setCurrentPayment(payment);
                    setConfirmationValue("");
                  }}
                  className={cn(
                    "flex items-center justify-between rounded-2xl border bg-muted/40 p-4 text-sm text-foreground/90 transition dark:bg-white/5 dark:text-white/80",
                    isActionable
                      ? "cursor-pointer hover:border-cyan-500/60 hover:bg-muted dark:hover:border-cyan-400/60 dark:hover:bg-white/10"
                      : "border-border opacity-80 dark:border-white/10",
                    isSelected
                      ? "border-cyan-500/80 shadow-lg shadow-cyan-500/15 dark:border-cyan-400/80 dark:shadow-cyan-500/20"
                      : "border-border dark:border-white/10"
                  )}>
                  <div>
                    <p className="font-semibold text-foreground dark:text-white">
                      {payment.amount} {payment.currency}
                    </p>
                    <p className="text-xs text-muted-foreground dark:text-white/60">
                      {payment.method === "card"
                        ? t("transactions.paymentsMethodCard")
                        : t("transactions.paymentsMethodCrypto")}{" "}
                      • {new Date(payment.createdAt).toLocaleString()}
                    </p>
                    {stripeFees && (
                      <p className="text-[11px] text-muted-foreground dark:text-white/40">
                        {t("transactions.paymentsFeeLabel", "Frais Stripe")}:{" "}
                        {stripeFees.fee.toFixed(2)} {stripeFees.currency} •{" "}
                        {t("transactions.paymentsNetLabel", "Net")}:{" "}
                        {stripeFees.net.toFixed(2)} {stripeFees.currency}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "rounded-full px-4 py-1 text-xs font-semibold uppercase",
                        payment.status === "confirmed"
                          ? "bg-green-500/20 text-green-800 dark:text-green-300"
                          : payment.status === "failed"
                          ? "bg-red-500/20 text-red-800 dark:text-red-300"
                          : payment.status === "cancelled"
                          ? "bg-gray-500/20 text-gray-700 dark:text-gray-300"
                          : "bg-yellow-500/20 text-yellow-900 dark:text-yellow-200"
                      )}>
                      {payment.status === "requires_action"
                        ? t("transactions.paymentsStatusRequiresAction")
                        : payment.status === "cancelled"
                        ? t("transactions.paymentsStatusCancelled", "annulé")
                        : payment.status}
                    </span>
                    {canCancel && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPayment(payment);
                          handleCancelPayment();
                        }}
                        className="text-xs">
                        {t("transactions.cancelPaymentButton", "Annuler")}
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
        {payments.length > paymentsPerPage && (
          <div className="mt-4 border-t border-border pt-3 dark:border-white/10">
            <Pagination
              currentPage={paymentsPage}
              totalPages={totalPaymentsPages}
              totalItems={payments.length}
              itemsPerPage={paymentsPerPage}
              onPageChange={setPaymentsPage}
              isLoading={loadingPayments}
              ariaLabelPrevious={t("transactions.pagination.previous")}
              ariaLabelNext={t("transactions.pagination.next")}
            />
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-border bg-muted/50 p-6 dark:border-white/10 dark:bg-black/30">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-semibold text-foreground dark:text-white">
            {t("transactions.historyTitle")}
          </h3>
          <span className="text-xs text-muted-foreground dark:text-white/60">
            {t("transactions.historyCountLabel", {
              count: transactions.length,
            })}
          </span>
        </div>

        {isLoading ? (
          <div className="mt-4 space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : transactions.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground dark:text-white/60">
            {t("transactions.historyEmpty")}
          </p>
        ) : (
          <>
            <div className="mt-4 hidden overflow-hidden rounded-2xl border border-border dark:border-white/10 md:block">
              <table className="min-w-full divide-y divide-border text-sm text-foreground/90 dark:divide-white/10 dark:text-white/80">
                <thead className="bg-muted text-muted-foreground dark:bg-white/5 dark:text-white/60">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">
                      {t("transactions.tableDate")}
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">
                      {t("transactions.tableType")}
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">
                      {t("transactions.tableAmount")}
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">
                      {t("transactions.tableStatus")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border dark:divide-white/10">
                  {paginatedTransactions.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="transition hover:bg-muted/60 dark:hover:bg-white/5">
                      <td className="px-4 py-3 text-muted-foreground dark:text-white/70">
                        {new Date(transaction.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {transaction.type}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {transaction.amount} {transaction.currency}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "rounded-full px-3 py-1 text-xs font-semibold uppercase",
                            transactionStatusPillClass(transaction.status)
                          )}>
                          {transactionStatusLabel(transaction.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 space-y-3 md:hidden">
              {paginatedTransactions.map((transaction) => (
                <article
                  key={transaction.id}
                  className="rounded-2xl border border-border bg-card p-4 text-sm text-foreground/90 dark:border-white/10 dark:bg-white/5 dark:text-white/80">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{transaction.type}</span>
                    <span className="text-xs text-muted-foreground dark:text-white/60">
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-lg font-semibold">
                      {transaction.amount} {transaction.currency}
                    </p>
                    <span
                      className={cn(
                        "rounded-full px-3 py-1 text-xs font-semibold uppercase",
                        transactionStatusPillClass(transaction.status)
                      )}>
                      {transactionStatusLabel(transaction.status)}
                    </span>
                  </div>
                </article>
              ))}
            </div>
            {transactions.length > transactionsPerPage && (
              <div className="mt-4 border-t border-border pt-3 dark:border-white/10">
                <Pagination
                  currentPage={transactionsPage}
                  totalPages={totalTransactionsPages}
                  totalItems={transactions.length}
                  itemsPerPage={transactionsPerPage}
                  onPageChange={setTransactionsPage}
                  isLoading={isLoading}
                  ariaLabelPrevious={t("transactions.pagination.previous")}
                  ariaLabelNext={t("transactions.pagination.next")}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de dépôt */}
      <DepositFormModal
        open={depositModalOpen}
        onClose={() => {
          setDepositModalOpen(false);
        }}
        onConfirm={handlePaymentInit}
        initialAmount={50}
        initialCurrency="USD"
        isProcessing={isSubmitting}
      />

      {stripePayment?.clientSecret && (
        <StripePaymentModal
          open={!!stripePayment}
          clientSecret={stripePayment.clientSecret}
          amount={stripePayment.amount}
          currency={stripePayment.currency}
          metadata={stripePayment.metadata}
          onClose={() => setStripePayment(null)}
          onSuccess={handleStripeSuccess}
        />
      )}
    </section>
  );
};

export default TransactionHistoryPage;
